import { Action } from 'redux';
import {
  GRPCFunction,
  GRPCRequestConfigRequest,
  RequestConfig,
  RequestConfigRequest,
} from '@fullstory/requests';
import { UID } from '@fullstory/util/uid';
import { Dictionary } from './utils/types';
import { createRequestAction } from './middleware/request/actions';

export type AnyRequestLifecycleAction = RequestLifecycleAction<any, any, any, any, any, any>;

// Any action with this type is handled by the middleware and is not passed to the reducers.
export const RequestActionType = 'REQUEST';
export const RequestActionQueueType = 'REQUEST_QUEUE';
export const RequestCancellationActionType = 'REQUEST_CANCELLATION';
export const AbortType = 'ABORT';
export const CancelPendingType = 'CANCEL_PENDING';

// The type of action that will be dispatched right before an HTTP request is made. It contains all
// of the necessary information for an HTTP client to send a request..
interface RequestAction<T, B> extends RequestConfigRequest<B>, Action<T> {
  maxAge?: number;
  force?: boolean;
  // If this is set to 'read', and force is not equal to true, requests that match the same cache key will
  // not re-fire request or success actions through redux.
  // WARNING: If you're sending requests that would make the server reply with an updated response,
  // and expect your reducer to automatically update, do not set this to 'read',
  // unless your reducer is also keeping track of the updates (likely a separate action), or if you have
  // force set to true on the outgoing fetch request.
  readWrite: 'read' | 'write';
}

// The type of action that will be dispatched when an HTTP request succeeds. The response data is
// attached to this action to pass into the reducers.
interface SuccessAction<T, B, D> extends Action<T> {
  request: RequestAction<T, B>;
  data: D;
}

export const isSuccessAction = (action: Action): action is SuccessAction<any, any, any> =>
  'data' in action && 'request' in action && isRequestLifecycleAction(action.request as Action);

// The type of action that will be dispatched when an HTTP request fails. The response data is
// attached to this action to pass into the reducers.
interface FailureAction<T, B> extends Action<T> {
  request: RequestAction<T, B>;
  error: any;
}

export const isFailureAction = (action: Action): action is FailureAction<any, any> =>
  'error' in action && 'request' in action && isRequestLifecycleAction(action.request as Action);

// This is the action type that the app will dispatch in order to trigger an HTTP request. It
// contains the request options and two action creators: one to be called on success, and the other
// on failure.
interface RequestLifecycleAction<R, S, F, B, D, M = D>
  extends RequestConfig<B, M>,
    Action<typeof RequestActionType> {
  request: RequestAction<R, B>;
  successType: S;
  failureType: F;
  id: UID;
  modify?: (data: D) => M;
}

export type RequestLifecycleActionReturn<R> = R extends RequestLifecycleAction<
  any,
  any,
  any,
  any,
  any,
  infer M
>
  ? M
  : never;

export function isRequestLifecycleAction(action: Action): action is AnyRequestLifecycleAction {
  return action.type === RequestActionType;
}

export interface RequestCancellationAction extends Action<typeof RequestCancellationActionType> {
  id: string;
}

export const isRequestCancellationAction = (action: Action): action is RequestCancellationAction =>
  action.type === RequestCancellationActionType;

// The union of action types that the middleware dispatches down the middleware stack.
export type DispatchedAction<R, S, F, B, D> = { sequenceNumber: number } & (
  | RequestAction<R, B>
  | SuccessAction<S, B, D>
  | FailureAction<F, B>
);

// Defines the type of the store's dispatch function after the middleware is applied. When passed
// the union of action types used by the reducers, this type takes the normal Redux `Dispatch`
// signature as well as the new signature that takes a `RequestLifecycleAction`. It works similarly
// to the `ThunkDispatch` type exported by redux-thunk. To properly type the store's dispatch
// function, intersect this type with the "dispatch" types supplied by other middleware, like this:
//
// type State = ReturnType<typeof reducer>
// type Action = { type: 'FOO', payload: string } | { type: 'BAR', payload: number }
// type Dispatch = ThunkDispatch<State, void, Action> & RequestDispatch<Action>
interface RequestDispatch<A extends Action> {
  <T extends A>(action: T): T;
  <M>(action: RequestLifecycleAction<any, any, any, any, any, M>): Promise<M>;
}

type RequestActionTypeSingle<A extends createRequestAction> =
  // Since a must extend a createRequestAction, infer the given generics (R, S, F, D, M)
  // and return the success/failure/action types
  ReturnType<A> extends RequestLifecycleAction<infer R, infer S, infer F, infer B, infer D, infer M>
    ? SuccessAction<S, B, M> | FailureAction<F, B> | RequestAction<R, B>
    : never;

export type RequestActionType<A> =
  // if A is an instance of direct createRequestAction, infer the Success/Action/Failure types
  A extends createRequestAction
    ? RequestActionTypeSingle<A>
    : // otherwise, it must be a dictionary, so recurse through it, adding it to a possible return type
    A extends Dictionary<any>
    ? { [K in keyof A]: RequestActionType<A[K]> }[keyof A]
    : never;

// The shape of the state produced by a request reducer
interface RequestState<D> {
  lastRequested: number;
  lastResponded: number;
  loading: boolean;
  error: boolean;
  data: D | null;
  safeMessage: string | null;
}

interface RequestQueueDispatch<A extends Action> {
  <T extends A>(action: T): T;
  (action: RequestQueueAction): RequestQueueAction;
  (action: RequestCancellationAction): RequestCancellationAction;
}

interface RequestQueueAction extends Action<typeof RequestActionQueueType> {
  queueType: typeof AbortType | typeof CancelPendingType;
  kind: string;
  excludeTags?: string[];
}

// GRPCRequestActions are a superset of RequestActions.
// When these functions are auto-generated, the server and client types
// can be kept in sync.
export interface GRPCRequestAction<T, TBody, TResponse>
  extends GRPCRequestConfigRequest<TBody, TResponse>,
    Omit<RequestAction<T, TBody>, 'buildRequest'> {}

export const isRequestQueueAction = (action: Action): action is RequestQueueAction =>
  action.type === RequestActionQueueType;

export function isGRPCRequestAction<T, TBody, TResponse>(
  req: RequestAction<T, TBody> | GRPCRequestAction<T, TBody, TResponse>,
): req is GRPCRequestAction<T, TBody, TResponse> {
  const castReq = req as GRPCRequestAction<T, TBody, TResponse>;
  return 'grpcFunction' in castReq;
}

/**
 * @deprecated this type is deprecated and will be removed in a future PR.
 *
 * When given a GRPCFunction, GRPCResponseType extracts the type of the
 * Promise, or what the final type that the server will return is.
 */
export type GRPCResponseType<G> = G extends GRPCFunction<infer TBody, infer TResponse>
  ? TResponse
  : never;

/**
 * @deprecated this type is deprecated and will be removed in a future PR.
 *
 * When given a GRPCFunction, GRPCBodyType extracts the payload that the GRPC endpoint
 * is expecting to get.
 */
export type GRPCBodyType<G> = G extends GRPCFunction<infer TBody, infer TResponse> ? TBody : never;

export type {
  RequestAction,
  SuccessAction,
  FailureAction,
  RequestLifecycleAction,
  RequestDispatch,
  RequestState,
  RequestQueueAction,
  RequestQueueDispatch,
};
